import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { idMonitoringI18n } from './i18n';

export default function MonitorAddModal({
  Modal,
  Input,
  Dropdown,
  show,
  onClose,
  onSave,
  clients = [],
}) {
  const { t } = useTranslation(undefined, { i18n: idMonitoringI18n });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (show) {
      setSearch('');
      setStatusFilter('all');
      setSelectedIds([]);
    }
  }, [show]);

  const statusOptions = useMemo(
    () => [
      { label: t('idMonitoring_status_all'), value: 'all' },
      { label: t('idMonitoring_status_active'), value: 'active' },
      { label: t('idMonitoring_status_inactive'), value: 'inactive' },
    ],
    [t]
  );

  const statusValue = useMemo(
    () =>
      statusOptions.find((option) => option.value === statusFilter) ||
      statusOptions[0],
    [statusFilter, statusOptions]
  );

  const filteredClients = useMemo(() => {
    const term = search.trim().toLowerCase();

    return clients.filter((client) => {
      const matchesSearch =
        !term ||
        [client.rut, client.name, client.email]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(term));

      const matchesStatus =
        statusFilter === 'all' || client.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [clients, search, statusFilter]);

  const toggleClient = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const allFilteredSelected =
    filteredClients.length > 0 &&
    filteredClients.every((client) => selectedIds.includes(client.id));

  const handleToggleAll = () => {
    if (allFilteredSelected) {
      const filteredIds = filteredClients.map((client) => client.id);
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
      return;
    }

    const filteredIds = filteredClients.map((client) => client.id);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
  };

  const availableCount = filteredClients.length;
  const selectedCount = selectedIds.length;

  const handleSave = () => {
    if (!selectedIds.length) return;
    const selectedClients = clients.filter((client) =>
      selectedIds.includes(client.id)
    );
    onSave?.(selectedClients);
  };

  const getStatusBadgeLabel = (status) =>
    status === 'active'
      ? t('idMonitoring_status_active')
      : t('idMonitoring_status_inactive');

  if (!Modal || !Input) return null;

  return (
    <Modal
      title={t('idMonitoring_modal_title')}
      show={show}
      onClose={onClose}
      primaryBtnTxt={t('idMonitoring_modal_add_button')}
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!selectedIds.length}
      secondaryBtnTxt={t('idMonitoring_modal_cancel_button')}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='960px'
    >
      <div className='d-flex flex-column gap-3'>
        <Input
          placeholder={t('idMonitoring_modal_search_placeholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <div className='row g-3 align-items-end'>
          <div className='col-12 col-md-6'>
            <label className='form-label mb-1'>
              {t('idMonitoring_modal_client_status_label')}
            </label>
            {Dropdown ? (
              <Dropdown
                options={statusOptions}
                value={statusValue}
                onChange={(option) => setStatusFilter(option?.value ?? 'all')}
                width='100%'
              />
            ) : (
              <select
                className='form-select'
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className='col-12 col-md-6 d-flex justify-content-between justify-content-md-end align-items-center'>
            <span className='color-gray-600 small'>
              {t('idMonitoring_modal_counts', {
                availableCount,
                selectedCount,
              })}
            </span>
            {availableCount > 0 && (
              <button
                type='button'
                className='btn btn-link btn-sm ms-3 p-0'
                onClick={handleToggleAll}
              >
                {allFilteredSelected
                  ? t('idMonitoring_modal_deselect_all')
                  : t('idMonitoring_modal_select_all')}
              </button>
            )}
          </div>
        </div>

        <div
          className='border rounded-3 p-2'
          style={{ maxHeight: '360px', overflowY: 'auto' }}
        >
          {clients.length === 0 && (
            <p className='color-gray-600 mb-0 small'>
              {t('idMonitoring_modal_empty_clients')}
            </p>
          )}

          {clients.length > 0 && filteredClients.length === 0 && (
            <p className='color-gray-600 mb-0 small'>
              {t('idMonitoring_modal_empty_filtered')}
            </p>
          )}

          {filteredClients.map((client) => {
            const isSelected = selectedIds.includes(client.id);
            const isActive = client.status === 'active';

            return (
              <div
                key={client.id}
                onClick={() => toggleClient(client.id)}
                className={`d-flex align-items-center justify-content-between rounded-3 px-3 py-2 mb-2 border ${
                  isSelected ? 'bg-primary-100' : 'bg-white'
                }`}
                style={{ cursor: 'pointer' }}
              >
                <div className='d-flex align-items-start gap-3'>
                  <div className='pt-1'>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      checked={isSelected}
                      onChange={() => toggleClient(client.id)}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </div>
                  <div>
                    <div className='d-flex align-items-center gap-2 mb-1'>
                      <span className='fw-semibold'>{client.name}</span>
                      <span
                        className={`badge rounded-pill ${
                          isActive
                            ? 'bg-success-subtle text-success'
                            : 'bg-danger-subtle text-danger'
                        }`}
                      >
                        {getStatusBadgeLabel(client.status)}
                      </span>
                    </div>
                    <div className='small color-gray-600'>
                      {client.rut && (
                        <span className='me-2'>
                          {t('idMonitoring_modal_rut_prefix')} {client.rut}
                        </span>
                      )}
                      {client.email && (
                        <span className='me-2'>{client.email}</span>
                      )}
                      {client.businessLine && (
                        <span>{client.businessLine}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
